import create from 'zustand';

export type EARTH_STAGE =
  | 'HIDDEN'
  | 'LANDING'
  | 'CENTERED'
  | 'DESTINATION_TRANSITION_START'
  | 'DESTINATION_TRANSITION_END'
  | 'OUTRO_TRANSITION_START'
  | 'OUTRO_TRANSITION_END';
export const PERIL_STAGES = [
  'HIDDEN',
  'flood',
  'storm',
  'bushfire',
  'cyclone',
] as const;
export type PERIL_STAGE = typeof PERIL_STAGES[number];
export type TILES_STAGE = 'HIDDEN' | 'VISIBLE';
export type CLOUDS_TRANSITION_STAGE = 'HIDDEN' | 'INTRO' | 'OUTRO';
export type INDICATION_RINGS_STAGE = 'HIDDEN' | 'VISIBLE';
export type BUILDING_STATE = 'HIDDEN' | 'VISIBLE';
export type SKY_STATE =
  | 'DAY'
  | 'NIGHT'
  | 'BUILDING'
  | 'TRANSPARENT'
  | 'BLACK'
  | 'CLOUDY'
  | 'STORMY'
  | 'BUSHFIRE'
  | 'FLOOD';
export type AUDIO_VISUALIZER_STAGE = 'HIDDEN' | 'FADING_IN' | 'VISIBLE';

export interface ThreeChildState<Stage> {
  stage: Stage;
  isRendered: boolean;
}

export interface ThreeStoreType {
  earth: Partial<ThreeChildState<EARTH_STAGE>>;
  setEarth: (earth: Partial<ThreeChildState<EARTH_STAGE>>) => void;
  tiles: Partial<ThreeChildState<TILES_STAGE>>;
  setTiles: (tiles: Partial<ThreeChildState<TILES_STAGE>>) => void;
  cloudsTransition: Partial<ThreeChildState<CLOUDS_TRANSITION_STAGE>>;
  setCloudsTransition: (
    clouds: Partial<ThreeChildState<CLOUDS_TRANSITION_STAGE>>
  ) => void;
  audioVisualizer: Partial<ThreeChildState<AUDIO_VISUALIZER_STAGE>>;
  setAudioVisualizer: (
    clouds: Partial<ThreeChildState<AUDIO_VISUALIZER_STAGE>>
  ) => void;
  isCanvasOverLayout: boolean;
  setCanvasOverLayout: (isCanvasOverLayout: boolean) => void;
  indicationRings: ThreeChildState<INDICATION_RINGS_STAGE>;
  setIndicationRings: (
    indicationRings: Partial<ThreeChildState<INDICATION_RINGS_STAGE>>
  ) => void;
  building: ThreeChildState<BUILDING_STATE>;
  setBuilding: (building: Partial<ThreeChildState<BUILDING_STATE>>) => void;
  sky: SKY_STATE;
  setSky: (sky: SKY_STATE) => void;
  peril: ThreeChildState<PERIL_STAGE>;
  setPeril: (peril: Partial<ThreeChildState<PERIL_STAGE>>) => void;
  weather: ThreeChildState<string>;
  setWeather: (weather: Partial<ThreeChildState<string>>) => void;
}

export const useThreeStore = create<ThreeStoreType>((set, get) => ({
  earth: {
    isRendered: true,
    stage: 'HIDDEN',
  },
  setEarth: earth => set({ earth: { ...get().earth, ...earth } }),
  tiles: {
    isRendered: false,
    stage: 'HIDDEN',
  },
  setTiles: tiles => set({ tiles: { ...get().tiles, ...tiles } }),
  cloudsTransition: {
    isRendered: false,
    stage: 'HIDDEN',
  },
  setCloudsTransition: clouds =>
    set({ cloudsTransition: { ...get().cloudsTransition, ...clouds } }),
  audioVisualizer: {
    isRendered: false,
    stage: 'HIDDEN',
  },
  setAudioVisualizer: audioVisualizer =>
    set({ audioVisualizer: { ...get().audioVisualizer, ...audioVisualizer } }),
  isCanvasOverLayout: false,
  setCanvasOverLayout: isCanvasOverLayout => set({ isCanvasOverLayout }),
  indicationRings: {
    isRendered: false,
    stage: 'HIDDEN',
  },
  setIndicationRings: indicationRings =>
    set({ indicationRings: { ...get().indicationRings, ...indicationRings } }),
  building: {
    isRendered: false,
    stage: 'HIDDEN',
  },
  setBuilding: building =>
    set({ building: { ...get().building, ...building } }),
  sky: 'NIGHT',
  setSky: sky => set({ sky: sky }),
  peril: {
    isRendered: false,
    stage: 'HIDDEN',
  },
  setPeril: peril => set({ peril: { ...get().peril, ...peril } }),
  weather: {
    isRendered: false,
    stage: 'HIDDEN',
  },
  setWeather: weather => set({ weather: { ...get().weather, ...weather } }),
}));
